import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import moment from "moment-timezone";
import PhoneField from "./PhoneField";
import {
  fetchEstimatedTimeCustomersSpend,
  fetchPlaceOfTable,
  fetchRestaurantDetails,
  fetchRestaurantOpenDays,
  fetchTables,
  getRestaurantNotificationsList
} from "../actions/restaurantActions";
import {
  createDefaultRestaurantSetupDraft,
  saveRestaurantSetupTourDraft,
  startRestaurantReservationTour,
  updateRestaurantSetupTourPage
} from "../utils/restaurantSetupTour";
import {
  getSetupApiErrorMessage,
  restaurantSetupApi
} from "../utils/restaurantSetupApi";
import {
  hasAgilFeatures,
  hasCompletoFeatures,
  UNLIMITED_DISPLAY_VALUE
} from "../utils/planFeatures";

const MAX_ROOMS = 20;
const MAX_TABLES_PER_ROOM = 100;
const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(31, 25, 26, 0.7);
  backdrop-filter: blur(3px);

  @media (max-width: 620px) {
    align-items: stretch;
    padding: 0;
  }
`;

const Modal = styled.div`
  width: min(820px, calc(100vw - 40px));
  height: min(760px, calc(100vh - 40px));
  height: min(760px, calc(100dvh - 40px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border-radius: 18px;
  border: 1px solid #eaded9;
  box-shadow: 0 24px 80px rgba(33, 25, 26, 0.35);
  font-family: "Poppins", sans-serif;

  @media (max-width: 620px) {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }
`;

const Header = styled.div`
  flex: 0 0 auto;
  padding: 18px 22px 14px;
  border-bottom: 1px solid #eee5e1;
  background: #fffaf7;
`;

const Eyebrow = styled.div`
  margin-bottom: 9px;
  color: #ee754c;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const Progress = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
`;

const ProgressItem = styled.div`
  min-width: 0;
  padding: 7px 8px;
  border-radius: 8px;
  background: ${({ $active, $done }) =>
    $active ? "#ee754c" : $done ? "#f8d8ca" : "#efeceb"};
  color: ${({ $active }) => ($active ? "#fff" : "#6c6162")};
  font-size: 0.72rem;
  font-weight: 750;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  padding: 24px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;

  @media (max-width: 620px) {
    padding: 20px 18px;
  }
`;

const Title = styled.h2`
  margin: 0 0 9px;
  color: #49383b;
  font-size: clamp(1.2rem, 3vw, 1.65rem);
  font-weight: 850;
  letter-spacing: -0.02em;
`;

const Description = styled.p`
  margin: 0 0 22px;
  color: #716567;
  font-size: 0.93rem;
  line-height: 1.55;
`;

const Footer = styled.div`
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 14px 22px 18px;
  border-top: 1px solid #eee5e1;
  background: #fff;

  @media (max-width: 620px) {
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  }
`;

const Button = styled.button`
  min-height: 40px;
  padding: 9px 15px;
  border: 0;
  border-radius: 9px;
  font-size: 0.86rem;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.48;
    cursor: default;
  }
`;

const BackButton = styled(Button)`
  background: #eef2f1;
  color: #586966;
`;

const NextButton = styled(Button)`
  margin-left: auto;
  background: #ee754c;
  color: white;
`;

const TextButton = styled(Button)`
  background: transparent;
  color: #8a7f81;
`;

const AddScheduleButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #f1f5f4;
  color: #526663;
`;

const AddIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  color: #ee754c;
  font-size: 1.1rem;
  font-weight: 900;
  line-height: 1;
`;

const Counter = styled.div`
  display: inline-grid;
  grid-template-columns: 42px minmax(65px, auto) 42px;
  align-items: stretch;
  overflow: hidden;
  border: 1px solid #d9d2cf;
  border-radius: 11px;
  background: #fff;
`;

const CounterButton = styled.button`
  border: 0;
  background: #f4f1ef;
  color: #ee754c;
  font-size: 1.35rem;
  font-weight: 850;
  cursor: pointer;

  &:disabled {
    color: #c9c1bf;
    cursor: default;
  }
`;

const CounterValue = styled.input`
  width: 100%;
  min-width: 65px;
  padding: 9px 6px;
  border: 0;
  outline: none;
  color: #49383b;
  font-size: 1rem;
  font-weight: 800;
  text-align: center;
  appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    margin: 0;
    appearance: none;
  }
`;

const Input = styled.input`
  width: 100%;
  min-height: 42px;
  padding: 9px 11px;
  border: 1px solid ${({ $invalid }) => ($invalid ? "#d9534f" : "#d9d2cf")};
  border-radius: 9px;
  outline: none;
  background: #fff;
  color: #49383b;
  font-size: 0.92rem;

  &:focus {
    border-color: #ee754c;
    box-shadow: 0 0 0 3px rgba(238, 117, 76, 0.13);
  }
`;

const Select = styled.select`
  width: 100%;
  min-height: 42px;
  padding: 9px 11px;
  border: 1px solid #d9d2cf;
  border-radius: 9px;
  outline: none;
  background: #fff;
  color: #49383b;
  font-size: 0.9rem;

  &:focus {
    border-color: #ee754c;
  }
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 7px;
  color: #54484a;
  font-size: 0.84rem;
  font-weight: 800;
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 2 }) => $columns}, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const RoomNameRow = styled.div`
  display: grid;
  grid-template-columns: 92px 1fr;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const RoomLabel = styled.div`
  color: #7a6e70;
  font-size: 0.82rem;
  font-weight: 750;
`;

const TablesScroll = styled.div`
  max-height: 290px;
  margin-top: 14px;
  padding-right: 4px;
  overflow-y: auto;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 14px;
  padding: 10px 12px;
  margin-bottom: 7px;
  border: 1px solid #ece5e2;
  border-radius: 10px;
  background: #fcfaf9;
`;

const Weekdays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 7px;

  @media (max-width: 680px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Weekday = styled.button`
  min-height: 42px;
  padding: 7px 5px;
  border: 1px solid ${({ $selected }) => ($selected ? "#ee754c" : "#ddd5d2")};
  border-radius: 9px;
  background: ${({ $selected }) => ($selected ? "#fff0e9" : "#fff")};
  color: ${({ $selected }) => ($selected ? "#d9603b" : "#675c5e")};
  font-size: 0.78rem;
  font-weight: 800;
  cursor: pointer;
`;

const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Choice = styled.button`
  min-height: 64px;
  padding: 10px;
  border: 2px solid ${({ $selected }) => ($selected ? "#ee754c" : "#e7dfdc")};
  border-radius: 11px;
  background: ${({ $selected }) => ($selected ? "#fff3ed" : "#fff")};
  color: #514547;
  font-size: 0.92rem;
  font-weight: 800;
  cursor: pointer;
`;

const SummaryList = styled.div`
  display: grid;
  gap: 9px;
  margin-bottom: 18px;
`;

const SummaryCard = styled.div`
  padding: 11px 13px;
  border: 1px solid #e7dfdc;
  border-radius: 10px;
  background: #fcfaf9;
  color: #605456;
  font-size: 0.86rem;
`;

const Toggle = styled.button`
  width: 58px;
  height: 31px;
  padding: 3px;
  border: 0;
  border-radius: 999px;
  background: ${({ $on }) => ($on ? "#ee754c" : "#cbc5c3")};
  cursor: pointer;
  transition: background 0.15s ease;

  &::after {
    content: "";
    display: block;
    width: 25px;
    height: 25px;
    border-radius: 50%;
    background: white;
    transform: translateX(${({ $on }) => ($on ? "27px" : "0")});
    transition: transform 0.15s ease;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 13px;
  border: 1px solid #ece5e2;
  border-radius: 11px;
  background: #fcfaf9;
`;

const ErrorBox = styled.div`
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 9px;
  background: #fff0ef;
  color: #a43d3a;
  font-size: 0.84rem;
  line-height: 1.45;
`;

const InfoBox = styled.div`
  margin-top: 14px;
  padding: 11px 13px;
  border-radius: 9px;
  background: #f2f6f5;
  color: #586765;
  font-size: 0.84rem;
  line-height: 1.5;
`;

const DefinitionValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const DefinitionUnit = styled.span`
  color: #615557;
  font-size: 0.92rem;
  font-weight: 800;
`;

const SavingBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 270px;
  text-align: center;
`;

const Spinner = styled.div`
  width: 42px;
  height: 42px;
  margin-bottom: 17px;
  border: 4px solid #f0dfd7;
  border-top-color: #ee754c;
  border-radius: 50%;
  animation: setup-spin 0.75s linear infinite;

  @keyframes setup-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Stepper = ({ value, onChange, min = 0, max = 999, step = 1 }) => {
  const normalized = Number.isFinite(Number(value)) ? Number(value) : min;
  const set = next => onChange(Math.min(max, Math.max(min, Number(next) || 0)));
  return (
    <Counter>
      <CounterButton
        type="button"
        onClick={() => set(normalized - step)}
        disabled={normalized <= min}
        aria-label="-"
      >
        −
      </CounterButton>
      <CounterValue
        type="number"
        min={min}
        max={max}
        step={step}
        value={normalized}
        onChange={event => set(event.target.value)}
      />
      <CounterButton
        type="button"
        onClick={() => set(normalized + step)}
        disabled={normalized >= max}
        aria-label="+"
      >
        +
      </CounterButton>
    </Counter>
  );
};

const durationLabel = (minutes, t) => {
  const value = Number(minutes) || 0;
  const hours = Math.floor(value / 60);
  const mins = value % 60;
  if (!hours) return t("ui.restaurantSetupWizard.minutesValue", { minutes: mins });
  if (!mins) return t("ui.restaurantSetupWizard.hoursValue", { hours });
  return t("ui.restaurantSetupWizard.hoursMinutesValue", { hours, minutes: mins });
};

const ensureRoomShape = (room, index) => ({
  name: room?.name ?? "",
  defaultSeats: Math.max(1, Number(room?.defaultSeats) || 2),
  tables:
    Array.isArray(room?.tables) && room.tables.length
      ? room.tables.map(table => ({ seats: Math.max(1, Number(table?.seats) || 2) }))
      : [{ seats: 2 }],
  _key: room?._key || `room-${index}-${Date.now()}`
});

const definitionKeys = [
  "limitPeopleReservation",
  "autoAcceptLimit",
  "autoAcceptMinHours",
  "autoAcceptFutureDays",
  "babyChairs",
  "babyChairsPerReservation",
  "timezone"
];

const RestaurantSetupWizard = ({ tour }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userInfo = useSelector(state => state.userLogin?.userInfo);
  const restaurantId = tour.restaurantId;
  const token = userInfo?.token;
  const [draft, setDraft] = useState(() => ({
    ...createDefaultRestaurantSetupDraft(),
    ...(tour.draft || {})
  }));
  const [pageKey, setPageKey] = useState(tour.pageKey || "roomCount");
  const [restaurant, setRestaurant] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const timezones = useMemo(() => moment.tz.names(), []);

  useEffect(() => {
    setPageKey(tour.pageKey || "roomCount");
  }, [tour.pageKey]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      saveRestaurantSetupTourDraft(restaurantId, draft);
    }, 100);
    return () => window.clearTimeout(timer);
  }, [draft, restaurantId]);

  useEffect(() => {
    let active = true;
    const loadRestaurant = async () => {
      try {
        const data = await restaurantSetupApi.getRestaurant(restaurantId, token);
        if (!active) return;
        setRestaurant(data);
        setDraft(current => {
          const defaults = createDefaultRestaurantSetupDraft();
          const currentDefs = current.definitions || defaults.definitions;
          const detectedTimezone =
            data?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Lisbon";
          const parseDurationHours = value => {
            if (typeof value === "number") return value;
            const hours = Number(String(value || "0").split(":")[0]);
            return Number.isFinite(hours) ? hours : 0;
          };

          return {
            ...current,
            definitions: {
              ...defaults.definitions,
              ...currentDefs,
              limitPeopleReservation:
                currentDefs.limitPeopleReservation ?? data?.limit_people_reservation ?? 8,
              autoAcceptLimit:
                currentDefs.autoAcceptLimit ?? data?.auto_accept_limit ?? 4,
              autoAcceptMinHours:
                currentDefs.autoAcceptMinHours ??
                parseDurationHours(data?.only_auto_accept_before_this_time),
              autoAcceptFutureDays:
                currentDefs.autoAcceptFutureDays ??
                Math.max(
                  0,
                  Math.round(
                    Number(
                      currentDefs.autoAcceptFutureHours ??
                        data?.only_auto_accept_before_this_time_future ??
                        720
                    ) / 24
                  )
                ),
              babyChairs:
                currentDefs.babyChairs ?? Number(data?.number_of_babychairs || 0),
              babyChairsPerReservation:
                currentDefs.babyChairsPerReservation ??
                Number(data?.limit_baby_chairs_reservation || 0),
              timezone: currentDefs.timezone || detectedTimezone
            }
          };
        });
      } catch (loadError) {
        if (active) setError(getSetupApiErrorMessage(loadError));
      }
    };
    loadRestaurant();
    return () => {
      active = false;
    };
  }, [restaurantId, token]);

  useEffect(() => {
    const preventEscape = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", preventEscape, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", preventEscape, true);
    };
  }, []);

  const updateDraft = updater => {
    setError("");
    setDraft(current =>
      typeof updater === "function" ? updater(current) : { ...current, ...updater }
    );
  };

  const go = key => {
    setError("");
    setPageKey(key);
    updateRestaurantSetupTourPage(restaurantId, key);
  };

  useEffect(() => {
    const [storedType, rawIndex] = String(pageKey).split(":");
    const storedIndex = Number(rawIndex);
    if (
      storedType === "definition" &&
      (!Number.isInteger(storedIndex) ||
        storedIndex < 0 ||
        storedIndex >= definitionKeys.length)
    ) {
      const safePage = `definition:${Math.max(0, definitionKeys.length - 1)}`;
      setPageKey(safePage);
      updateRestaurantSetupTourPage(restaurantId, safePage);
    }
  }, [pageKey, restaurantId]);

  const setRoomCount = count => {
    const safeCount = Math.min(MAX_ROOMS, Math.max(1, Number(count) || 1));
    updateDraft(current => {
      const rooms = Array.from({ length: safeCount }, (_, index) =>
        ensureRoomShape(current.rooms?.[index], index)
      );
      return { ...current, roomCount: safeCount, rooms };
    });
  };

  const setRoomName = (index, value) => {
    updateDraft(current => ({
      ...current,
      rooms: current.rooms.map((room, roomIndex) =>
        roomIndex === index ? { ...room, name: value } : room
      )
    }));
  };

  const setRoomTablesCount = (roomIndex, count) => {
    const safeCount = Math.min(
      MAX_TABLES_PER_ROOM,
      Math.max(1, Number(count) || 1)
    );
    updateDraft(current => ({
      ...current,
      rooms: current.rooms.map((room, index) => {
        if (index !== roomIndex) return room;
        const tables = Array.from({ length: safeCount }, (_, tableIndex) => ({
          seats: Math.max(
            1,
            Number(room.tables?.[tableIndex]?.seats) || Number(room.defaultSeats) || 2
          )
        }));
        return { ...room, tables };
      })
    }));
  };

  const setRoomDefaultSeats = (roomIndex, seats) => {
    const safeSeats = Math.max(1, Math.min(100, Number(seats) || 1));
    updateDraft(current => ({
      ...current,
      rooms: current.rooms.map((room, index) =>
        index === roomIndex
          ? {
              ...room,
              defaultSeats: safeSeats,
              tables: room.tables.map(() => ({ seats: safeSeats }))
            }
          : room
      )
    }));
  };

  const setTableSeats = (roomIndex, tableIndex, seats) => {
    const safeSeats = Math.max(1, Math.min(100, Number(seats) || 1));
    updateDraft(current => ({
      ...current,
      rooms: current.rooms.map((room, index) =>
        index === roomIndex
          ? {
              ...room,
              tables: room.tables.map((table, currentTableIndex) =>
                currentTableIndex === tableIndex ? { ...table, seats: safeSeats } : table
              )
            }
          : room
      )
    }));
  };

  const updateSchedule = (scheduleIndex, patch) => {
    updateDraft(current => ({
      ...current,
      schedules: current.schedules.map((schedule, index) =>
        index === scheduleIndex ? { ...schedule, ...patch } : schedule
      )
    }));
  };

  const toggleScheduleDay = (scheduleIndex, day) => {
    const schedule = draft.schedules[scheduleIndex];
    const days = schedule.days.includes(day)
      ? schedule.days.filter(item => item !== day)
      : [...schedule.days, day];
    updateSchedule(scheduleIndex, { days });
  };

  const addSchedule = () => {
    const newIndex = draft.schedules.length;
    updateDraft(current => ({
      ...current,
      schedules: [
        ...current.schedules,
        { days: [], startTime: "19:00", endTime: "23:00" }
      ]
    }));
    go(`scheduleDays:${newIndex}`);
  };

  const updateDefinition = (key, value) => {
    updateDraft(current => ({
      ...current,
      definitions: {
        ...current.definitions,
        [key]: value
      }
    }));
  };

  const parsePage = () => {
    const [type, rawIndex] = String(pageKey).split(":");
    return { type, index: Number(rawIndex) || 0 };
  };

  const { type: pageType, index: pageIndex } = parsePage();

  const roomNamesValid = () => {
    const names = draft.rooms.map(room => String(room.name || "").trim());
    return (
      names.every(Boolean) &&
      new Set(names.map(name => name.toLocaleLowerCase())).size === names.length
    );
  };

  const currentScheduleValid = index => {
    const schedule = draft.schedules[index];
    if (!schedule || !schedule.days?.length) return false;
    if (!schedule.startTime || !schedule.endTime) return false;
    return schedule.startTime < schedule.endTime;
  };

  const emailValid = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || ""));

  const currentDefinitionIndex = pageType === "definition" ? pageIndex : -1;
  const currentDefinitionKey = definitionKeys[currentDefinitionIndex];
  const rawPlanLevel = Number(restaurant?.sub_lvl);
  const isLegacyDefinitionBlocked =
    Boolean(restaurant) && [0, 1].includes(rawPlanLevel);
  const definitionIsPlanBlocked = [
    "limitPeopleReservation",
    "autoAcceptLimit",
    "autoAcceptMinHours",
    "autoAcceptFutureDays"
  ].includes(currentDefinitionKey)
    ? Boolean(restaurant) && !hasAgilFeatures(restaurant)
    : ["babyChairs", "babyChairsPerReservation"].includes(currentDefinitionKey)
      ? isLegacyDefinitionBlocked
      : false;
  const slotCapacityIsPlanBlocked =
    Boolean(restaurant) && !hasCompletoFeatures(restaurant);

  const sectionIndex = (() => {
    if (["roomCount", "roomNames", "roomTables"].includes(pageType)) return 0;
    if (
      [
        "scheduleDays",
        "scheduleTime",
        "slotInterval",
        "slotCapacity",
        "moreSchedules",
        "averageStay"
      ].includes(pageType)
    )
      return 1;
    if (pageType === "definition") return 2;
    return 3;
  })();

  const validateCurrent = () => {
    if (pageType === "roomCount") return Number(draft.roomCount) > 0;
    if (pageType === "roomNames") {
      if (!roomNamesValid()) {
        setError(t("ui.restaurantSetupWizard.errors.roomNames"));
        return false;
      }
    }
    if (pageType === "roomTables") {
      const room = draft.rooms[pageIndex];
      if (!room?.tables?.length || room.tables.some(table => Number(table.seats) <= 0)) {
        setError(t("ui.restaurantSetupWizard.errors.tables"));
        return false;
      }
    }
    if (pageType === "scheduleDays") {
      if (!draft.schedules[pageIndex]?.days?.length) {
        setError(t("ui.restaurantSetupWizard.errors.weekday"));
        return false;
      }
    }
    if (pageType === "scheduleTime") {
      const schedule = draft.schedules[pageIndex];
      if (!schedule?.startTime || !schedule?.endTime || schedule.startTime >= schedule.endTime) {
        setError(t("ui.restaurantSetupWizard.errors.scheduleTime"));
        return false;
      }
    }
    if (
      pageType === "slotCapacity" &&
      !slotCapacityIsPlanBlocked &&
      Number(draft.slotCapacity) <= 0
    ) {
      setError(t("ui.restaurantSetupWizard.errors.capacity"));
      return false;
    }
    if (pageType === "email" && draft.emailNotifications.enabled) {
      if (!emailValid(draft.emailNotifications.email)) {
        setError(t("ui.restaurantSetupWizard.errors.email"));
        return false;
      }
    }
    if (pageType === "phone" && draft.phoneNotifications.enabled && !isPhoneValid) {
      setError(t("ui.restaurantSetupWizard.errors.phone"));
      return false;
    }
    return true;
  };

  const getNextPage = () => {
    if (pageType === "roomCount") return "roomNames";
    if (pageType === "roomNames") return "roomTables:0";
    if (pageType === "roomTables") {
      return pageIndex < draft.rooms.length - 1
        ? `roomTables:${pageIndex + 1}`
        : "scheduleDays:0";
    }
    if (pageType === "scheduleDays") return `scheduleTime:${pageIndex}`;
    if (pageType === "scheduleTime") {
      return pageIndex === 0 ? "slotInterval" : "moreSchedules";
    }
    if (pageType === "slotInterval") return "slotCapacity";
    if (pageType === "slotCapacity") return "moreSchedules";
    if (pageType === "moreSchedules") return "averageStay";
    if (pageType === "averageStay") return "definition:0";
    if (pageType === "definition") {
      return pageIndex < definitionKeys.length - 1
        ? `definition:${pageIndex + 1}`
        : "phone";
    }
    if (pageType === "phone") return "email";
    return null;
  };

  const getPreviousPage = () => {
    if (pageType === "roomCount") return null;
    if (pageType === "roomNames") return "roomCount";
    if (pageType === "roomTables") {
      return pageIndex > 0 ? `roomTables:${pageIndex - 1}` : "roomNames";
    }
    if (pageType === "scheduleDays") {
      return pageIndex === 0
        ? `roomTables:${Math.max(0, draft.rooms.length - 1)}`
        : "moreSchedules";
    }
    if (pageType === "scheduleTime") return `scheduleDays:${pageIndex}`;
    if (pageType === "slotInterval") return "scheduleTime:0";
    if (pageType === "slotCapacity") return "slotInterval";
    if (pageType === "moreSchedules") {
      return draft.schedules.length > 1
        ? `scheduleTime:${draft.schedules.length - 1}`
        : "slotCapacity";
    }
    if (pageType === "averageStay") return "moreSchedules";
    if (pageType === "definition") {
      return pageIndex > 0 ? `definition:${pageIndex - 1}` : "averageStay";
    }
    if (pageType === "phone") return `definition:${definitionKeys.length - 1}`;
    if (pageType === "email") return "phone";
    return null;
  };

  const handleNext = () => {
    setError("");
    if (!validateCurrent()) return;
    const next = getNextPage();
    if (next) go(next);
  };

  const handleBack = () => {
    const previous = getPreviousPage();
    if (previous) go(previous);
  };

  const saveAll = async (draftOverride = null, skipCurrentValidation = false) => {
    const effectiveDraft = draftOverride || draft;
    setError("");
    if (!skipCurrentValidation && !validateCurrent()) return;
    setIsSaving(true);

    try {
      const latestRestaurant = await restaurantSetupApi.getRestaurant(restaurantId, token);
      const roomNames = effectiveDraft.rooms.map(room => room.name.trim());
      const blocked = !hasAgilFeatures(latestRestaurant);
      const legacyDefinitionsBlocked = [0, 1].includes(
        Number(latestRestaurant?.sub_lvl)
      );
      const intervalBlocked = !hasCompletoFeatures(latestRestaurant);
      await restaurantSetupApi.ensureRoomsAndTables(restaurantId, effectiveDraft.rooms, token);
      await restaurantSetupApi.ensureSchedules(
        restaurantId,
        effectiveDraft.schedules,
        {
          slotInterval: effectiveDraft.slotInterval,
          slotCapacity: effectiveDraft.slotCapacity,
          preserveSlotCapacity: intervalBlocked,
          fallbackSlotCapacity: latestRestaurant?.accept_people_per_each_time
        },
        roomNames,
        token
      );

      const definitions = effectiveDraft.definitions;
      const settingsPayload = {
        each_time: effectiveDraft.slotInterval,
        timezone:
          definitions.timezone ||
          latestRestaurant?.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      if (!intervalBlocked) {
        settingsPayload.accept_people_per_each_time = Number(
          effectiveDraft.slotCapacity
        );
      }

      if (!blocked) {
        settingsPayload.limit_people_reservation = Number(
          definitions.limitPeopleReservation
        );
        settingsPayload.auto_accept_limit = Number(definitions.autoAcceptLimit);
        settingsPayload.only_auto_accept_before_this_time = `${String(
          Number(definitions.autoAcceptMinHours) || 0
        ).padStart(2, "0")}:00`;
        settingsPayload.only_auto_accept_before_this_time_future =
          Number(definitions.autoAcceptFutureDays) * 24;
      }

      if (!legacyDefinitionsBlocked) {
        settingsPayload.number_of_babychairs = Number(definitions.babyChairs);
        settingsPayload.limit_baby_chairs_reservation = Number(
          definitions.babyChairsPerReservation
        );
      }

      const updatedRestaurant = await restaurantSetupApi.patchRestaurant(
        restaurantId,
        settingsPayload,
        token
      );

      const configuredLimit = blocked
        ? Number(updatedRestaurant?.limit_people_reservation || latestRestaurant?.limit_people_reservation)
        : Number(definitions.limitPeopleReservation);
      const fallbackMaxSeats = Math.max(
        1,
        ...effectiveDraft.rooms.flatMap(room => room.tables.map(table => Number(table.seats) || 1))
      );

      await restaurantSetupApi.ensureAverageStay(
        restaurantId,
        effectiveDraft.averageStayMinutes,
        configuredLimit > 0 ? configuredLimit : fallbackMaxSeats,
        token
      );

      await restaurantSetupApi.ensureNotifications(
        restaurantId,
        effectiveDraft,
        updatedRestaurant?.restaurant_name || latestRestaurant?.restaurant_name,
        token
      );

      await Promise.all([
        dispatch(fetchRestaurantDetails(restaurantId)),
        dispatch(fetchPlaceOfTable(restaurantId)),
        dispatch(fetchTables(restaurantId)),
        dispatch(fetchRestaurantOpenDays(restaurantId)),
        dispatch(fetchEstimatedTimeCustomersSpend(restaurantId)),
        dispatch(getRestaurantNotificationsList(restaurantId))
      ]);

      startRestaurantReservationTour(restaurantId);
    } catch (saveError) {
      console.error("Restaurant setup wizard save failed:", saveError);
      setError(
        t("ui.restaurantSetupWizard.errors.save", {
          error: getSetupApiErrorMessage(saveError)
        })
      );
      setIsSaving(false);
    }
  };

  const renderDefinition = () => {
    const defs = draft.definitions;
    const title = t(`ui.restaurantSetupWizard.definitions.${currentDefinitionKey}.title`);
    const body = t(`ui.restaurantSetupWizard.definitions.${currentDefinitionKey}.body`);

    if (definitionIsPlanBlocked) {
      return (
        <>
          <Title>{title}</Title>
          <Description>{body}</Description>
          {currentDefinitionKey === "limitPeopleReservation" && (
            <DefinitionValueRow>
              <Stepper
                value={UNLIMITED_DISPLAY_VALUE}
                onChange={() => {}}
                min={UNLIMITED_DISPLAY_VALUE}
                max={UNLIMITED_DISPLAY_VALUE}
              />
              <DefinitionUnit>
                {t("ui.restaurantSetupWizard.definitionUnits.people")}
              </DefinitionUnit>
            </DefinitionValueRow>
          )}
          <InfoBox>{t("ui.restaurantSetupWizard.planLocked")}</InfoBox>
        </>
      );
    }

    const numberDefinition = (
      value,
      onChange,
      min = 0,
      max = 1000,
      unitKey = null
    ) => (
      <DefinitionValueRow>
        <Stepper value={value} onChange={onChange} min={min} max={max} />
        {unitKey && (
          <DefinitionUnit>
            {t(`ui.restaurantSetupWizard.definitionUnits.${unitKey}`)}
          </DefinitionUnit>
        )}
      </DefinitionValueRow>
    );

    let control = null;
    switch (currentDefinitionKey) {
      case "limitPeopleReservation":
        control = numberDefinition(
          defs.limitPeopleReservation,
          value => updateDefinition("limitPeopleReservation", value),
          1,
          100,
          "people"
        );
        break;
      case "autoAcceptLimit":
        control = numberDefinition(
          defs.autoAcceptLimit,
          value => updateDefinition("autoAcceptLimit", value),
          0,
          100,
          "people"
        );
        break;
      case "autoAcceptMinHours":
        control = numberDefinition(
          defs.autoAcceptMinHours,
          value => updateDefinition("autoAcceptMinHours", value),
          0,
          720,
          "hours"
        );
        break;
      case "autoAcceptFutureDays":
        control = numberDefinition(
          defs.autoAcceptFutureDays,
          value => updateDefinition("autoAcceptFutureDays", value),
          0,
          365,
          "days"
        );
        break;
      case "babyChairs":
        control = numberDefinition(
          defs.babyChairs,
          value => updateDefinition("babyChairs", value),
          0,
          100,
          "highChairs"
        );
        break;
      case "babyChairsPerReservation":
        control = numberDefinition(
          defs.babyChairsPerReservation,
          value => updateDefinition("babyChairsPerReservation", value),
          0,
          20,
          "highChairs"
        );
        break;
      case "timezone":
        control = (
          <Select
            value={defs.timezone || ""}
            onChange={event => updateDefinition("timezone", event.target.value)}
          >
            {timezones.map(timezone => (
              <option key={timezone} value={timezone}>
                {timezone}
              </option>
            ))}
          </Select>
        );
        break;
      default:
        break;
    }

    return (
      <>
        <Title>{title}</Title>
        <Description>{body}</Description>
        {control}
      </>
    );
  };

  const renderPage = () => {
    if (isSaving) {
      return (
        <SavingBox>
          <Spinner />
          <Title>{t("ui.restaurantSetupWizard.saving.title")}</Title>
          <Description>{t("ui.restaurantSetupWizard.saving.body")}</Description>
          {error && <ErrorBox>{error}</ErrorBox>}
        </SavingBox>
      );
    }

    if (pageType === "roomCount") {
      return (
        <>
          <Title>{t("ui.restaurantSetupWizard.rooms.count.title")}</Title>
          <Description>{t("ui.restaurantSetupWizard.rooms.count.body")}</Description>
          <Stepper value={draft.roomCount} onChange={setRoomCount} min={1} max={MAX_ROOMS} />
        </>
      );
    }

    if (pageType === "roomNames") {
      return (
        <>
          <Title>{t("ui.restaurantSetupWizard.rooms.names.title")}</Title>
          <Description>{t("ui.restaurantSetupWizard.rooms.names.body")}</Description>
          {draft.rooms.map((room, index) => (
            <RoomNameRow key={room._key || index}>
              <RoomLabel>
                {t("ui.restaurantSetupWizard.rooms.names.room", { number: index + 1 })}
              </RoomLabel>
              <Input
                type="text"
                value={room.name}
                placeholder={t("ui.restaurantSetupWizard.rooms.names.placeholder", {
                  number: index + 1
                })}
                onChange={event => setRoomName(index, event.target.value)}
              />
            </RoomNameRow>
          ))}
        </>
      );
    }

    if (pageType === "roomTables") {
      const room = draft.rooms[pageIndex];
      return (
        <>
          <Title>
            {t("ui.restaurantSetupWizard.tables.title", {
              room: room?.name || pageIndex + 1
            })}
          </Title>
          <Description>{t("ui.restaurantSetupWizard.tables.body")}</Description>
          <Grid>
            <Field>
              <FieldLabel>{t("ui.restaurantSetupWizard.tables.count")}</FieldLabel>
              <Stepper
                value={room.tables.length}
                onChange={value => setRoomTablesCount(pageIndex, value)}
                min={1}
                max={MAX_TABLES_PER_ROOM}
              />
            </Field>
            <Field>
              <FieldLabel>{t("ui.restaurantSetupWizard.tables.defaultSeats")}</FieldLabel>
              <Stepper
                value={room.defaultSeats}
                onChange={value => setRoomDefaultSeats(pageIndex, value)}
                min={1}
                max={100}
              />
            </Field>
          </Grid>
          <FieldLabel>{t("ui.restaurantSetupWizard.tables.review")}</FieldLabel>
          <TablesScroll>
            {room.tables.map((table, tableIndex) => (
              <TableRow key={tableIndex}>
                <div>
                  <strong>
                    {t("ui.restaurantSetupWizard.tables.table", {
                      number: tableIndex + 1
                    })}
                  </strong>
                  <div style={{ fontSize: "0.76rem", color: "#8a7f81" }}>
                    {t("ui.restaurantSetupWizard.tables.seats")}
                  </div>
                </div>
                <Stepper
                  value={table.seats}
                  onChange={value => setTableSeats(pageIndex, tableIndex, value)}
                  min={1}
                  max={100}
                />
              </TableRow>
            ))}
          </TablesScroll>
        </>
      );
    }

    if (pageType === "scheduleDays") {
      const schedule = draft.schedules[pageIndex];
      return (
        <>
          <Title>
            {pageIndex === 0
              ? t("ui.restaurantSetupWizard.schedule.days.title")
              : t("ui.restaurantSetupWizard.schedule.days.additionalTitle", {
                  number: pageIndex + 1
                })}
          </Title>
          <Description>{t("ui.restaurantSetupWizard.schedule.days.body")}</Description>
          <Weekdays>
            {WEEKDAYS.map(day => (
              <Weekday
                key={day}
                type="button"
                $selected={schedule.days.includes(day)}
                onClick={() => toggleScheduleDay(pageIndex, day)}
              >
                {t(`ui.restaurantSetupWizard.weekdays.${day}`)}
              </Weekday>
            ))}
          </Weekdays>
        </>
      );
    }

    if (pageType === "scheduleTime") {
      const schedule = draft.schedules[pageIndex];
      return (
        <>
          <Title>{t("ui.restaurantSetupWizard.schedule.time.title")}</Title>
          <Description>{t("ui.restaurantSetupWizard.schedule.time.body")}</Description>
          <Grid>
            <Field>
              <FieldLabel>{t("ui.restaurantSetupWizard.schedule.time.start")}</FieldLabel>
              <Input
                type="time"
                value={schedule.startTime}
                onChange={event => updateSchedule(pageIndex, { startTime: event.target.value })}
              />
            </Field>
            <Field>
              <FieldLabel>{t("ui.restaurantSetupWizard.schedule.time.end")}</FieldLabel>
              <Input
                type="time"
                value={schedule.endTime}
                onChange={event => updateSchedule(pageIndex, { endTime: event.target.value })}
              />
            </Field>
          </Grid>
        </>
      );
    }

    if (pageType === "slotInterval") {
      return (
        <>
          <Title>{t("ui.restaurantSetupWizard.schedule.interval.title")}</Title>
          <Description>{t("ui.restaurantSetupWizard.schedule.interval.body")}</Description>
          <ChoiceGrid>
            {["00:15", "00:30", "00:45"].map(value => (
              <Choice
                type="button"
                key={value}
                $selected={draft.slotInterval === value}
                onClick={() => updateDraft({ ...draft, slotInterval: value })}
              >
                {t("ui.restaurantSetupWizard.minutesValue", {
                  minutes: Number(value.split(":")[1])
                })}
              </Choice>
            ))}
          </ChoiceGrid>
        </>
      );
    }

    if (pageType === "slotCapacity") {
      const intervalMinutes = Number(draft.slotInterval.split(":")[1]);
      return (
        <>
          <Title>
            {t("ui.restaurantSetupWizard.schedule.capacity.title", {
              minutes: intervalMinutes
            })}
          </Title>
          <Description>{t("ui.restaurantSetupWizard.schedule.capacity.body")}</Description>
          <Stepper
            value={
              slotCapacityIsPlanBlocked
                ? UNLIMITED_DISPLAY_VALUE
                : draft.slotCapacity
            }
            onChange={
              slotCapacityIsPlanBlocked
                ? () => {}
                : value => updateDraft({ ...draft, slotCapacity: value })
            }
            min={slotCapacityIsPlanBlocked ? UNLIMITED_DISPLAY_VALUE : 1}
            max={slotCapacityIsPlanBlocked ? UNLIMITED_DISPLAY_VALUE : 1000}
          />
          {slotCapacityIsPlanBlocked && (
            <InfoBox>{t("ui.restaurantSetupWizard.planLocked")}</InfoBox>
          )}
        </>
      );
    }

    if (pageType === "moreSchedules") {
      return (
        <>
          <Title>{t("ui.restaurantSetupWizard.schedule.more.title")}</Title>
          <Description>{t("ui.restaurantSetupWizard.schedule.more.body")}</Description>
          <SummaryList>
            {draft.schedules.map((schedule, index) => (
              <SummaryCard key={index}>
                <strong>
                  {t("ui.restaurantSetupWizard.schedule.more.schedule", {
                    number: index + 1
                  })}
                </strong>
                <div>
                  {schedule.days
                    .map(day => t(`ui.restaurantSetupWizard.weekdays.${day}`))
                    .join(", ")}
                </div>
                <div>
                  {schedule.startTime} – {schedule.endTime}
                </div>
              </SummaryCard>
            ))}
          </SummaryList>
          <AddScheduleButton type="button" onClick={addSchedule}>
            <AddIcon aria-hidden="true">+</AddIcon>
            {t("ui.restaurantSetupWizard.schedule.more.add")}
          </AddScheduleButton>
        </>
      );
    }

    if (pageType === "averageStay") {
      return (
        <>
          <Title>{t("ui.restaurantSetupWizard.averageStay.title")}</Title>
          <Description>{t("ui.restaurantSetupWizard.averageStay.body")}</Description>
          <div style={{ display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap" }}>
            <Stepper
              value={draft.averageStayMinutes}
              onChange={value =>
                updateDraft({
                  ...draft,
                  averageStayMinutes: value
                })
              }
              min={15}
              max={600}
              step={15}
            />
            <strong style={{ color: "#ee754c" }}>
              {durationLabel(draft.averageStayMinutes, t)}
            </strong>
          </div>
          <InfoBox>{t("ui.restaurantSetupWizard.averageStay.note")}</InfoBox>
        </>
      );
    }

    if (pageType === "definition") {
      return renderDefinition();
    }

    if (pageType === "phone") {
      const phone = draft.phoneNotifications;
      return (
        <>
          <Title>{t("ui.restaurantSetupWizard.notifications.phone.title")}</Title>
          <Description>{t("ui.restaurantSetupWizard.notifications.phone.body")}</Description>
          <ToggleRow style={{ marginBottom: "16px" }}>
            <Toggle
              type="button"
              $on={phone.enabled}
              onClick={() =>
                updateDraft(current => ({
                  ...current,
                  phoneNotifications: {
                    ...current.phoneNotifications,
                    enabled: !current.phoneNotifications.enabled
                  }
                }))
              }
            />
            <strong>
              {phone.enabled
                ? t("ui.restaurantSetupWizard.yes")
                : t("ui.restaurantSetupWizard.no")}
            </strong>
          </ToggleRow>
          {phone.enabled && (
            <PhoneField
              countryCodeNumber={phone.countryCode}
              setCountryCodeNumber={value =>
                updateDraft(current => ({
                  ...current,
                  phoneNotifications: {
                    ...current.phoneNotifications,
                    countryCode: value
                  }
                }))
              }
              phoneWithoutCountryCode={phone.number}
              setPhoneWithoutCountryCode={value =>
                updateDraft(current => ({
                  ...current,
                  phoneNotifications: {
                    ...current.phoneNotifications,
                    number: value
                  }
                }))
              }
              isPhoneValid={isPhoneValid}
              setIsPhoneValid={setIsPhoneValid}
              initialFullPhone={
                phone.number ? `+${phone.countryCode}${phone.number}` : undefined
              }
            />
          )}
          <InfoBox>{t("ui.restaurantSetupWizard.notifications.phone.note")}</InfoBox>
        </>
      );
    }

    if (pageType === "email") {
      const email = draft.emailNotifications;
      return (
        <>
          <Title>{t("ui.restaurantSetupWizard.notifications.email.title")}</Title>
          <Description>{t("ui.restaurantSetupWizard.notifications.email.body")}</Description>
          <ToggleRow style={{ marginBottom: "16px" }}>
            <Toggle
              type="button"
              $on={email.enabled}
              onClick={() =>
                updateDraft(current => ({
                  ...current,
                  emailNotifications: {
                    ...current.emailNotifications,
                    enabled: !current.emailNotifications.enabled
                  }
                }))
              }
            />
            <strong>
              {email.enabled
                ? t("ui.restaurantSetupWizard.yes")
                : t("ui.restaurantSetupWizard.no")}
            </strong>
          </ToggleRow>
          {email.enabled && (
            <Input
              type="email"
              value={email.email}
              placeholder={t("ui.restaurantSetupWizard.notifications.email.placeholder")}
              onChange={event =>
                updateDraft(current => ({
                  ...current,
                  emailNotifications: {
                    ...current.emailNotifications,
                    email: event.target.value
                  }
                }))
              }
            />
          )}
          <InfoBox>{t("ui.restaurantSetupWizard.notifications.email.note")}</InfoBox>
        </>
      );
    }

    return null;
  };

  const previousPage = getPreviousPage();
  const isFinalInputPage = pageType === "email";

  return (
    <Overlay role="presentation">
      <Modal role="dialog" aria-modal="true" aria-label={t("ui.restaurantSetupWizard.ariaLabel")}>
        <Header>
          <Eyebrow>{t("ui.restaurantSetupWizard.setupTitle")}</Eyebrow>
          <Progress>
            {["rooms", "schedule", "definitions", "notifications"].map((section, index) => (
              <ProgressItem key={section} $active={sectionIndex === index} $done={sectionIndex > index}>
                {t(`ui.restaurantSetupWizard.sections.${section}`)}
              </ProgressItem>
            ))}
          </Progress>
        </Header>

        <Body>
          {renderPage()}
          {error && !isSaving && <ErrorBox>{error}</ErrorBox>}
        </Body>

        {!isSaving && (
          <Footer>
            <BackButton type="button" onClick={handleBack} disabled={!previousPage}>
              {t("ui.restaurantSetupWizard.back")}
            </BackButton>

            {(pageType === "phone" || pageType === "email") && (
              <TextButton
                type="button"
                onClick={() => {
                  if (pageType === "phone") {
                    updateDraft(current => ({
                      ...current,
                      phoneNotifications: {
                        ...current.phoneNotifications,
                        enabled: false,
                        number: ""
                      }
                    }));
                    go("email");
                  } else {
                    const nextDraft = {
                      ...draft,
                      emailNotifications: {
                        ...draft.emailNotifications,
                        enabled: false,
                        email: ""
                      }
                    };
                    updateDraft(nextDraft);
                    saveRestaurantSetupTourDraft(restaurantId, nextDraft);
                    saveAll(nextDraft, true);
                  }
                }}
              >
                {t("ui.restaurantSetupWizard.skipThisQuestion")}
              </TextButton>
            )}

            <NextButton type="button" onClick={isFinalInputPage ? () => saveAll() : handleNext}>
              {isFinalInputPage
                ? t("ui.restaurantSetupWizard.saveAndContinue")
                : t("ui.restaurantSetupWizard.next")}
            </NextButton>
          </Footer>
        )}
      </Modal>
    </Overlay>
  );
};

export default RestaurantSetupWizard;
